/**
 * SurveyEditor — VER-108 → VER-109 full-power builder.
 *
 * A section-based Formik editor: survey details, a shared Likert scale, ordered
 * sections of typed questions (add / reorder / duplicate / delete), branching
 * rules, scoring, a live participant preview (reuses SurveyRunner) and a
 * draft → publish control gated by Yup validation ("cannot publish until valid").
 *
 * System seeds (isSystemSeed) open READ-ONLY with a "Clone to edit" affordance —
 * the WHO-5 / UCLA reference scales are never mutated in place.
 *
 * FRONTEND-ONLY — save/publish lift the definition to the parent, which persists
 * it via the injected callbacks (see builder.persistence.ts + INTEGRATION.md).
 */
import React, { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    FormControlLabel,
    Grid,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import {
    AddOutlined,
    ContentCopyOutlined,
    PublishOutlined,
    SaveOutlined,
    VisibilityOutlined,
} from "@mui/icons-material";
import { Formik, useFormikContext } from "formik";
import type { Section, SurveyDefinition } from "./survey.types";
import { allQuestions } from "./survey.types";
import { surveyEditorSchema } from "./survey.schema";
import { deriveRawRange } from "./scoring";
import { duplicateSection, newSection } from "./builder.mock";
import { arrayInsert, arrayMove, arrayRemove, renumber, useDragReorder } from "./reorder";
import { OptionsEditor } from "./OptionsEditor";
import { SectionEditor } from "./SectionEditor";
import { BranchingEditor } from "./BranchingEditor";
import { SurveyRunner } from "./SurveyRunner";
import { useFieldError } from "./useFieldError";

export type SurveyEditorProps = {
    survey: SurveyDefinition;
    /** Persist the current definition as a draft. */
    onSave: (survey: SurveyDefinition) => void;
    /** Validate + persist + publish (bumps version). Only reachable when valid. */
    onPublish: (survey: SurveyDefinition) => void;
    onCancel: () => void;
    /** Clone the (read-only) seed into an editable copy. */
    onClone: (survey: SurveyDefinition) => void;
};

/** Collect all Yup messages (empty = valid) for the publish-gate summary. */
const collectIssues = (survey: SurveyDefinition): string[] => {
    try {
        surveyEditorSchema.validateSync(survey, { abortEarly: false });
        return [];
    } catch (err) {
        const anyErr = err as { inner?: { message: string }[]; message?: string };
        if (anyErr.inner && anyErr.inner.length > 0) {
            return Array.from(new Set(anyErr.inner.map((e) => e.message)));
        }
        return anyErr.message ? [anyErr.message] : ["Survey is not valid"];
    }
};

// ---------------------------------------------------------------------------
// Read-only seed view
// ---------------------------------------------------------------------------
const SeedReadonly = ({
    survey,
    onCancel,
    onClone,
}: Pick<SurveyEditorProps, "survey" | "onCancel" | "onClone">): JSX.Element => (
    <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle1" component="span">
                {survey.title} is a read-only reference scale
            </Typography>
            <Typography variant="body2" display="block" mt={0.5}>
                Reference scales cannot be edited in place. Clone it to create an editable copy.
            </Typography>
        </Alert>

        <Typography variant="body2" color="textSecondary" mb={2}>
            {survey.instructions}
        </Typography>

        {survey.sections.map((section) => (
            <Box key={section.id} mb={2}>
                <Typography variant="subtitle2" color="textPrimary" mb={1}>
                    {section.title} ({section.questions.length} questions)
                </Typography>
                {section.questions.map((q, index) => (
                    <Typography key={q.id} variant="body2" color="textSecondary" mb={0.5}>
                        {index + 1}. {q.text}
                        {q.reverseScored ? " (reverse-scored)" : ""}
                    </Typography>
                ))}
            </Box>
        ))}

        {survey.licence ? (
            <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="textSecondary">
                    Licence: {survey.licence}
                </Typography>
            </>
        ) : null}

        <Box display="flex" justifyContent="space-between" pt={3}>
            <Button variant="outlined" onClick={onCancel}>
                Close
            </Button>
            <Button
                variant="contained"
                color="primary"
                startIcon={<ContentCopyOutlined />}
                onClick={() => onClone(survey)}
            >
                Clone to edit
            </Button>
        </Box>
    </Box>
);

// ---------------------------------------------------------------------------
// Editable body (inside Formik; uses context — no render-prop hooks)
// ---------------------------------------------------------------------------
const SectionHeading = ({ children }: { children: React.ReactNode }): JSX.Element => (
    <Typography variant="h6" mb={2}>
        {children}
    </Typography>
);

const SurveyEditorBody = ({
    onSave,
    onPublish,
    onCancel,
}: Pick<SurveyEditorProps, "onSave" | "onPublish" | "onCancel">): JSX.Element => {
    const { values, handleChange, handleBlur, setFieldValue } = useFormikContext<SurveyDefinition>();
    const fieldError = useFieldError();
    const [preview, setPreview] = useState(false);

    const issues = collectIssues(values);
    const canPublish = issues.length === 0;

    const setSections = (next: Section[]) => setFieldValue("sections", renumber(next));
    const sectionDrag = useDragReorder((from, to) => setSections(arrayMove(values.sections, from, to)));

    const finalise = (survey: SurveyDefinition): SurveyDefinition => ({
        ...survey,
        scoring: { ...survey.scoring, rawRange: deriveRawRange(survey) },
    });

    const statusColor = values.status === "published" ? "success" : "default";
    const versionText =
        values.status === "published"
            ? `Published · v${values.version}`
            : values.version > 0
              ? "Draft (unpublished changes)"
              : "Draft";

    // Remount the preview only when the STRUCTURE changes (not on every keystroke),
    // so answers persist across text edits but reset when questions/types change.
    const previewKey = values.sections
        .map((s) => `${s.id}:${s.questions.map((q) => `${q.id}.${q.type}.${q.options?.length ?? 0}`).join(",")}`)
        .join("|");

    return (
        <Box>
            {/* Status bar */}
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Chip size="small" color={statusColor} label={versionText} />
                    <Typography variant="body2" color="textSecondary">
                        {allQuestions(values).length} questions · {values.sections.length} section
                        {values.sections.length === 1 ? "" : "s"}
                    </Typography>
                </Box>
                <Button variant="outlined" startIcon={<VisibilityOutlined />} onClick={() => setPreview((p) => !p)}>
                    {preview ? "Hide preview" : "Show preview"}
                </Button>
            </Box>

            <Grid container spacing={4}>
                <Grid item xs={12} md={preview ? 7 : 12}>
                    {/* Details */}
                    <SectionHeading>Survey details</SectionHeading>
                    <Grid container spacing={2} mb={4}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                name="code"
                                label="Code"
                                value={values.code}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(fieldError("code"))}
                                helperText={fieldError("code") ?? "Short machine code, e.g. WHO5"}
                            />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                fullWidth
                                name="title"
                                label="Title"
                                value={values.title}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(fieldError("title"))}
                                helperText={fieldError("title")}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="instructions"
                                label="Instructions"
                                multiline
                                minRows={2}
                                maxRows={5}
                                value={values.instructions}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(fieldError("instructions"))}
                                helperText={fieldError("instructions")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                name="scaleCode"
                                label="Scale code (optional)"
                                value={values.scaleCode ?? ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                helperText="Link to a known clinical scale"
                            />
                        </Grid>
                        <Grid item xs={12} sm={8} display="flex" alignItems="center">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={values.higherIsBetter}
                                        onChange={(e) => setFieldValue("higherIsBetter", e.target.checked)}
                                    />
                                }
                                label="A higher score is better (for reporting)"
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ mb: 4 }} />

                    {/* Shared scale */}
                    <SectionHeading>Shared Likert scale</SectionHeading>
                    <Typography variant="body2" color="textSecondary" mb={2}>
                        Rating and single-choice questions use this scale unless they define their own options.
                    </Typography>
                    {typeof (fieldError("defaultScale.options") as string | undefined) === "string" ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {fieldError("defaultScale.options")}
                        </Alert>
                    ) : null}
                    <Box mb={4}>
                        <OptionsEditor name="defaultScale.options" minOptions={2} addLabel="Add scale option" />
                    </Box>

                    <Divider sx={{ mb: 4 }} />

                    {/* Sections */}
                    <SectionHeading>Sections</SectionHeading>
                    <Box display="flex" flexDirection="column" gap={3} mb={3}>
                        {values.sections.map((section, si) => (
                            <Box key={section.id} {...sectionDrag.dropZoneProps(si)}>
                                <SectionEditor
                                    sectionIndex={si}
                                    section={section}
                                    reorder={{
                                        onUp: () => setSections(arrayMove(values.sections, si, si - 1)),
                                        onDown: () => setSections(arrayMove(values.sections, si, si + 1)),
                                        disableUp: si === 0,
                                        disableDown: si === values.sections.length - 1,
                                        dragHandleProps: sectionDrag.handleProps(si),
                                    }}
                                    onDuplicate={() =>
                                        setSections(
                                            arrayInsert(values.sections, si + 1, duplicateSection(section, si + 2)),
                                        )
                                    }
                                    onDelete={() => setSections(arrayRemove(values.sections, si))}
                                    canDelete={values.sections.length > 1}
                                />
                            </Box>
                        ))}
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<AddOutlined />}
                        onClick={() => setSections([...values.sections, newSection(values.sections.length + 1)])}
                    >
                        Add section
                    </Button>

                    <Divider sx={{ my: 4 }} />

                    {/* Branching */}
                    <SectionHeading>Branching &amp; logic</SectionHeading>
                    <BranchingEditor />

                    <Divider sx={{ my: 4 }} />

                    {/* Scoring */}
                    <SectionHeading>Scoring (optional)</SectionHeading>
                    <Grid container spacing={2} mb={2}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                type="number"
                                name="scoring.multiplier"
                                label="Multiplier"
                                value={values.scoring.multiplier ?? ""}
                                onChange={(e) =>
                                    setFieldValue(
                                        "scoring.multiplier",
                                        e.target.value === "" ? undefined : Number(e.target.value),
                                    )
                                }
                                onBlur={handleBlur}
                                error={Boolean(fieldError("scoring.multiplier"))}
                                helperText={fieldError("scoring.multiplier") ?? "e.g. WHO-5 uses ×4"}
                            />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField
                                fullWidth
                                type="number"
                                name="scoring.cutoff.belowValue"
                                label="Cut-off below"
                                value={values.scoring.cutoff?.belowValue ?? ""}
                                onChange={(e) => {
                                    if (e.target.value === "") {
                                        setFieldValue("scoring.cutoff", undefined);
                                        return;
                                    }
                                    setFieldValue("scoring.cutoff", {
                                        belowValue: Number(e.target.value),
                                        label: values.scoring.cutoff?.label ?? "",
                                    });
                                }}
                                onBlur={handleBlur}
                                error={Boolean(fieldError("scoring.cutoff.belowValue"))}
                                helperText={fieldError("scoring.cutoff.belowValue")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                name="scoring.cutoff.label"
                                label="Cut-off guidance label"
                                value={values.scoring.cutoff?.label ?? ""}
                                onChange={(e) =>
                                    setFieldValue("scoring.cutoff", {
                                        belowValue: values.scoring.cutoff?.belowValue ?? 0,
                                        label: e.target.value,
                                    })
                                }
                                onBlur={handleBlur}
                                disabled={!values.scoring.cutoff}
                                error={Boolean(fieldError("scoring.cutoff.label"))}
                                helperText={
                                    fieldError("scoring.cutoff.label") ?? "Neutral wellbeing guidance, not a diagnosis"
                                }
                            />
                        </Grid>
                    </Grid>
                    <Typography variant="caption" color="textSecondary">
                        Derived raw-score range: {deriveRawRange(values)[0]}–{deriveRawRange(values)[1]}
                    </Typography>
                </Grid>

                {/* Live preview */}
                {preview ? (
                    <Grid item xs={12} md={5}>
                        <Box
                            sx={{
                                position: { md: "sticky" },
                                top: { md: 16 },
                                p: { xs: 2, sm: 3 },
                                border: "1px solid",
                                borderColor: "borderShade.main",
                                borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                                maxHeight: { md: "80vh" },
                                overflowY: { md: "auto" },
                            }}
                        >
                            <Typography variant="overline" color="textSecondary">
                                Participant preview
                            </Typography>
                            <SurveyRunner key={previewKey} survey={values} />
                        </Box>
                    </Grid>
                ) : null}
            </Grid>

            {/* Publish gate + actions */}
            <Divider sx={{ my: 4 }} />
            {!canPublish ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" component="span">
                        {issues.length} issue{issues.length === 1 ? "" : "s"} to resolve before publishing
                    </Typography>
                    <Box component="ul" sx={{ m: 0, mt: 1, pl: 3 }}>
                        {issues.slice(0, 6).map((issue) => (
                            <Typography key={issue} component="li" variant="body2">
                                {issue}
                            </Typography>
                        ))}
                    </Box>
                </Alert>
            ) : (
                <Alert severity="success" sx={{ mb: 2 }}>
                    This survey is valid and ready to publish.
                </Alert>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Button variant="outlined" onClick={onCancel}>
                    Cancel
                </Button>
                <Box display="flex" gap={2}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<SaveOutlined />}
                        onClick={() => onSave(finalise(values))}
                    >
                        Save draft
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PublishOutlined />}
                        disabled={!canPublish}
                        onClick={() => onPublish(finalise(values))}
                    >
                        Publish
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export const SurveyEditor = ({ survey, onSave, onPublish, onCancel, onClone }: SurveyEditorProps): JSX.Element => {
    if (survey.isSystemSeed) {
        return <SeedReadonly survey={survey} onCancel={onCancel} onClone={onClone} />;
    }

    return (
        <Formik
            initialValues={survey}
            validationSchema={surveyEditorSchema}
            validateOnChange={false}
            validateOnBlur
            enableReinitialize
            onSubmit={() => {
                /* submission handled via explicit Save/Publish buttons */
            }}
        >
            <SurveyEditorBody onSave={onSave} onPublish={onPublish} onCancel={onCancel} />
        </Formik>
    );
};
